import type * as React from "react";

import { AvatarMenu, TopBar } from "@don/design-system";

import { KNOWLEDGE, SESSIONS, SETTINGS, navigate, type Route } from "../routing/route.js";

/**
 * The Console's frame: the top bar over a scrolling body (issue 192).
 *
 * Two tabs, Sessions and Knowledge (ux-spec 17, 38: Knowledge always shows). Knowledge goes to
 * `/knowledge` and is current there and on `/knowledge/{id}` (issue 277). Settings and Sign out sit behind the avatar, not as tabs;
 * Settings is offered in every build since issue 212 put Settings · Team in production
 * (e2e `shell.spec.ts`). A session keeps Sessions current; Settings
 * and Not found mark no tab. Sign-in has no top bar.
 *
 * The wordmark is one constant, so a rename is one diff (CONTEXT.md).
 */
export const WORDMARK = "Don";

const TABS = [
  { id: "sessions", label: "Sessions", href: SESSIONS },
  { id: "knowledge", label: "Knowledge", href: KNOWLEDGE },
] as const;

/** The tab you are on: a session belongs to Sessions; Settings and Not found have none. */
function currentTab(route: Route): string | null {
  if (route.kind === "sessions" || route.kind === "session") return "sessions";
  if (route.kind === "knowledge") return "knowledge";
  return null;
}

export function ConsoleShell({
  route,
  onSignOut,
  children,
}: {
  route: Route;
  onSignOut: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        background: "var(--ground)",
        color: "var(--ink-1)",
      }}
    >
      {route.kind !== "sign-in" && (
        <TopBar
          wordmark={WORDMARK}
          current={currentTab(route)}
          tabs={TABS.map((tab) => ({ ...tab, onSelect: () => navigate(tab.href) }))}
          end={
            <AvatarMenu
              onSettings={() => navigate(SETTINGS)}
              onSignOut={onSignOut}
            />
          }
        />
      )}
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        {children}
      </div>
    </div>
  );
}
